import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { IAuthState } from './auth-state';
import { AuthDataService } from '@core/data/api/auth/auth-data.service';
import {
    AUTH_USER_SET,
    AUTH_COMPANY_SET,
    LOGIN,
    LOGIN_SUCCESSFUL,
    LOGOUT,
    LOGOUT_POST,
    LOGIN_COMPANY,
    PASSWORD_REQUEST_VERIFICATION_CODE,
    PASSWORD_SUBMIT_VERIFICATION_CODE,
    PASSWORD_RESET,
    ROUTER_NAVIGATION
} from '../action-types';
import {
    LoginAction,
    LoginCompanyAction,
    LoginErrorAction,
    LoginSuccessAction,
    LogoutAction,
    PostLogoutAction
} from './login/login-actions';
import {
    Observable,
    concatMap,
    finalize,
    from,
    mergeMap,
    switchMap,
    tap, 
    withLatestFrom
} from 'rxjs';
import { ossCatchError } from '@core/operators/catch-error.operator';
import {
    DeleteAuthAction,
    InvalidateAccessTokenAction,
    InvalidateRefreshTokenAction,
    SetUserAuthAction,
    SetCompanyAuthAction,
    RequestVerificationCodeAction,
    SubmitVerificationCodeAction,
    ResetPasswordAction
} from './auth-actions';
import { AuthSession } from '@core/models/auth/auth-session';
import { LocalStorageService } from '@core/local-storage/local-storage.service';
import { STORE_LOGIN_PREFS, STORE_USER_AUTH } from '@core/local-storage/local-storage';
import { ContinueAuthenticateDictionary } from '@core/models/auth/continue-authenticate-dictionary';
import { RouterGoAction, RouterNavigationCancelAction } from '../router/router-actions';
import {
    DEVSUPPORT, LIVEVIEW, LOGIN as LOGIN_STATE,
    PASSWORD_VALIDATION_REQUEST,
    PASSWORD_VALIDATION_SUBMIT
} from '../../router/app-states';
import { AccountType } from '@core/models/account/account-type';
import { getUserAuthentication } from './auth-selectors';
import {
    ClearSessionAction,
    FetchUserAction,
    InitSessionAction
} from '../session/session-actions';
import { IAppRouterState } from '../router/router-state';
import { SetToastAction } from '../toast/toast.actions';
import { Toast } from '@core/models/toast/toast';
import { ToastType } from '@core/models/toast/toast-type';
import { ShutdownIntercomAction } from '../intercom/intercom-actions';
import { AccountRole } from '@core/models/account/account-role';
import { initialDeliveryOrdersFiltersState } from '../deliveries/filters/deliveries-filters.state';
import { SetDeliveryOrdersFilterByAction } from '../deliveries/deliveries-actions';
import { initialLiveViewOrdersFiltersState } from '../live-view/filters/live-view-filters.state';
import { SetLiveViewOrdersFilterByAction } from '../live-view/live-view-actions';

@Injectable()
export class AuthEffects {

    constructor(private store$: Store<IAuthState>,
                private actions$: Actions,
                private localStorageService: LocalStorageService,
                private authDataService: AuthDataService) {
    }

    login$ = createEffect(() =>
        this.actions$.pipe(
            ofType<LoginAction>(LOGIN),
            switchMap((action: LoginAction) => {

                const authCredentials = action.payload;
                let remoteCall: Observable<any>;

                if (authCredentials.email) {

                    remoteCall = this.authDataService.loginByPassword(authCredentials);

                } else if (authCredentials.phone) {
                    remoteCall = this.authDataService.loginByPhone({
                        password: authCredentials.password,
                        username: authCredentials.phone
                    });
                }


                return remoteCall.pipe(
                    switchMap((loginResponse) => {

                        this.localStorageService.setItem(STORE_LOGIN_PREFS, !!authCredentials.email ? 'email' : 'phone')

                        return [
                            (action as any).success(),
                            new LoginSuccessAction(loginResponse)
                        ];
                    }),
                    ossCatchError(([errorActions, errorResponse]) => {
                        if (errorResponse.error?.status === 'failed' && errorResponse.error?.error === 'errors.user_suspended') {
                            return from([
                                (action as any).error({
                                    ...errorResponse,
                                    isAccountSuspended: true
                                }),
                                new LoginErrorAction()
                            ]);

                        } else {

                            return from([
                                (action as any).error(errorResponse),
                                new LoginErrorAction()
                            ]);
                        }
                    })
                )
            })
        )
    );

    loginSuccess$ = createEffect(() => 
        this.actions$.pipe(
            ofType<LoginSuccessAction>(LOGIN_SUCCESSFUL),
            switchMap((loginResponse) => {

                const auth: AuthSession = loginResponse.payload;

                if (!!auth.cad) {
                    const companyCredentials: ContinueAuthenticateDictionary = auth.cad;
                    return this.authDataService.loginByAccessToken(companyCredentials.company_id, auth)
                        .pipe(
                            concatMap((companyLoginResponse) => {

                                const userAuth: AuthSession = {
                                    ...auth,
                                    _profile_type: AccountType.USER
                                }

                                const companyAuth: AuthSession = {
                                    ...companyLoginResponse,
                                    _profile_type: AccountType.COMPANY,
                                    _profile_id: companyCredentials.company_id
                                };

                                return [
                                    new SetUserAuthAction(userAuth), 
                                    new SetCompanyAuthAction(companyAuth),
                                    new InitSessionAction({ isLogin: true })
                                ];
                                
                            }),
                            ossCatchError(([errorActions]) => from(errorActions))
                        );

                } else if (auth.role === AccountRole.SUPPORT) {

                    // Redirect support account to company selector
                    const userAuth: AuthSession = {
                        ...auth,
                        _profile_type: AccountType.USER
                    }

                    return [
                        new SetUserAuthAction(userAuth),
                        new FetchUserAction(),
                        new RouterGoAction({ name: DEVSUPPORT })
                    ];

                } else {
                    return [new SetToastAction(new Toast(ToastType.ERROR, 'Login.Error.NoPermission'))];
                }
            })
        )
    );

    loginCompany = createEffect(() =>
        this.actions$.pipe(
            ofType<LoginCompanyAction>(LOGIN_COMPANY),
            withLatestFrom(this.store$.select(getUserAuthentication)),
            switchMap(([action, userAuth]: [LoginCompanyAction, AuthSession]) => {

                const companyId: string = action.payload.companyId;
                const isChangingCompanies: boolean = action.payload.isChangingCompanies;

                return this.authDataService.loginByAccessToken(action.payload.companyId, userAuth, isChangingCompanies)
                    .pipe(
                        switchMap((companyLoginResponse) => {

                            const updatedUserAuth: AuthSession = {
                                ...userAuth,
                                _profile_type: AccountType.USER,
                                cad: {
                                    ...userAuth.cad,
                                    company_id: companyId
                                }
                            }

                            const companyAuth: AuthSession = {
                                ...companyLoginResponse,
                                _profile_type: AccountType.COMPANY,
                                _profile_id: companyId
                            };

                            return [
                                new SetDeliveryOrdersFilterByAction({
                                    ...initialDeliveryOrdersFiltersState,
                                    status: []
                                }),
                                new SetLiveViewOrdersFilterByAction({
                                    ...initialLiveViewOrdersFiltersState
                                }),
                                new SetUserAuthAction(updatedUserAuth), 
                                new SetCompanyAuthAction(companyAuth),
                                new InitSessionAction({
                                    isLogin: true,
                                    bypassRedirection: action.payload.bypassRedirection }),
                                (action as any).success()
                            ];
                        }),
                        ossCatchError(([errorActions, errorResponse]) => from([
                            (action as any).error()
                        ].concat(errorActions)))
                    );
            })
        )
    );

    logout$ = createEffect(() => {
        return this.actions$.pipe(
            ofType<LogoutAction>(LOGOUT),
            switchMap(() => {
                return this.authDataService.logout()
                    .pipe(
                        mergeMap(() => {
                            return [
                                new PostLogoutAction()
                            ];
                        }),
                        ossCatchError(([errorActions, errorResponse]) => {
                            return from(errorActions.concat(
                                [new PostLogoutAction()]
                            ))
                        })
                    );
            })
        )
    });

    postLogout$ = createEffect(() => {
        return this.actions$.pipe(
            ofType<PostLogoutAction>(LOGOUT_POST),
            switchMap(() => {

                // Remove everything from the account here
                this.localStorageService.removeItem(STORE_USER_AUTH);

                // Get last login prefs
                const loginMode = this.localStorageService.getItem(STORE_LOGIN_PREFS);
                const params = { mode: loginMode === 'email' ? loginMode : null };

                return [
                    new DeleteAuthAction(),
                    new ClearSessionAction(),
                    new ShutdownIntercomAction(),
                    new RouterGoAction({
                        name: LOGIN_STATE,
                        params
                    })
                ];
            })
        );
    });

    requestVerificationCode$ = createEffect(() => 
        this.actions$.pipe(
            ofType<RequestVerificationCodeAction>(PASSWORD_REQUEST_VERIFICATION_CODE),
            switchMap((action: RequestVerificationCodeAction) => {

                const { phone, email } = action.payload;

                return this.authDataService.requestVerificationCode(email, phone)
                    .pipe(
                        switchMap((response) => {

                            return [(action as any).success()]
                        }),
                        ossCatchError(([_, errorResponse]) => {
                            return from([
                                (action as any).error(errorResponse)
                            ]);
                        })
                    );
            })
        )
    );

    submitVerificationCode$ = createEffect(() => 
        this.actions$.pipe(
            ofType<SubmitVerificationCodeAction>(PASSWORD_SUBMIT_VERIFICATION_CODE),
            switchMap((action: SubmitVerificationCodeAction) => {

                const { code, id } = action.payload;

                return this.authDataService.submitVerificationCode(code, id)
                    .pipe(
                        switchMap((response) => {

                            return [(action as any).success()];
                        }),
                        ossCatchError(([errorActions, errorResponse]) => {

                            return from([
                                (action as any).error(errorResponse)
                            ]);
                        })
                    );
            })
        )
    );

    resetPassword$ = createEffect(() => 
        this.actions$.pipe(
            ofType<ResetPasswordAction>(PASSWORD_RESET),
            switchMap((action: ResetPasswordAction) => {

                const { password, id, code } = action.payload;

                return this.authDataService.resetPassword(password, code, id)
                    .pipe(
                        switchMap((response) => {

                            return [(action as any).success()];
                        }),
                        ossCatchError(([errorActions, errorResponse]) => {

                            return from(errorActions.concat([
                                (action as any).error(errorResponse)
                            ]));
                        })
                    );
            })
        )
    );

    setUserAuth$ = createEffect(() =>
        this.actions$.pipe(
            ofType<SetCompanyAuthAction>(AUTH_USER_SET),
            tap((action: SetCompanyAuthAction) => {

                const auth = this.localStorageService.getItem(STORE_USER_AUTH);

                this.localStorageService.setItem(STORE_USER_AUTH, {
                    ...auth,
                    user: action.payload
                });
            })
        )
    , { dispatch: false });

    setCompanyAuth$ = createEffect(() =>
        this.actions$.pipe(
            ofType<SetCompanyAuthAction>(AUTH_COMPANY_SET),
            tap((action: SetCompanyAuthAction) => {

                const auth = this.localStorageService.getItem(STORE_USER_AUTH);

                this.localStorageService.setItem(STORE_USER_AUTH, {
                    ...auth, 
                    company: action.payload
                });
            })
        )
    , { dispatch: false });

    invalidateAccessToken$ = createEffect(() => {
        return this.actions$.pipe(
            ofType<InvalidateAccessTokenAction>('INVALIDATE_ACCESS_TOKEN'),
            withLatestFrom(this.store$.select(getUserAuthentication)),
            switchMap(([action, userAuth]) => {
                const isUser: boolean = !!action.payload?.isUser;
                const userAuthHeader: string = 'Bearer ' + userAuth.access_token;

                return this.authDataService.deauth(false, isUser, userAuthHeader)
                    .pipe(
                        mergeMap((deauthorisationResponse) => {
                            return [];
                        }),
                        ossCatchError(([errorActions]) => from(errorActions))
                    );
            })
        )
    });

    invalidateRefreshToken$ = createEffect(() => {
        return this.actions$.pipe(
            ofType<InvalidateRefreshTokenAction>('INVALIDATE_REFRESH_TOKEN'),
            withLatestFrom(this.store$.select(getUserAuthentication)),
            switchMap(([action, userAuth]) => {

                const isUser: boolean = !!action.payload?.isUser;
                const userAuthHeader: string = 'Bearer ' + userAuth.access_token;

                return this.authDataService.deauth(true, isUser, userAuthHeader)
                    .pipe(
                        mergeMap((deauthorisationResponse) => {
                            return [];
                        }),
                        ossCatchError(([errorActions]) => from(errorActions))
                    );
            })
        )
    });

    authRouter$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ROUTER_NAVIGATION),
            mergeMap((action: Action) => {
                const actions: Action[] = [];
                const routerState: IAppRouterState = (action as any).payload
                        .routerState as IAppRouterState;
                    const { name: stateName, fromState, params } = routerState;

        
                if (stateName === PASSWORD_VALIDATION_SUBMIT) {
                    if (fromState.name !== PASSWORD_VALIDATION_REQUEST) {

                        actions.push(new RouterNavigationCancelAction({ state: PASSWORD_VALIDATION_SUBMIT }));
                        actions.push(new RouterGoAction({ name: PASSWORD_VALIDATION_REQUEST }));
                    }
                }

                return actions;
            })
        );
    })
}